import React, { useState } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { X, Save, Package, Loader2 } from 'lucide-react';
import itemService from '../../services/itemService';

const QuickAddItemModal = ({ isOpen, onClose, onSave }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    price: '', 
    stock: '',
    item_type: 'product',
    unit: 'PCS'
  });

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert(t(LangKeys.item_name_required || 'Item name is required'));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        item_type: formData.item_type,
        unit: formData.unit,
        sale_price: parseFloat(formData.price || 0),
        purchase_price: parseFloat(formData.price || 0),
        opening_stock: parseFloat(formData.stock || 0),
        tax_included: 1
      };

      const success = await itemService.createItem(payload);
      if (success) {
        // Fetch items again or just return the constructed item for the bill
        // For the bill, we need it in the format expected by AddBillView
        const newBillItem = {
          item: { ...payload, id: Date.now() }, // Mock ID if API doesn't return one immediately
          name: payload.name,
          qty: 1,
          price: payload.sale_price,
          originalPrice: payload.sale_price,
          item_type: payload.item_type
        };
        onSave(newBillItem);
      } else {
        alert(t(LangKeys.failed_to_save || 'Failed to save item'));
      }
    } catch (error) {
      console.error('Quick add failed:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
           <h3 style={styles.title}>{t(LangKeys.add_bill_add_new_item)}</h3>
           <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>
        
        <div style={styles.content}>
           <div style={styles.inputField}>
              <label style={styles.label}>{t(LangKeys.add_bill_item_name || 'Item Name')}</label>
              <input 
                style={styles.input} 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})} 
                placeholder="e.g. Cement Bag" 
              />
           </div>
           <div style={styles.row}>
              <div style={styles.inputField}>
                 <label style={styles.label}>{t(LangKeys.add_bill_item_price || 'Price')}</label>
                 <input 
                   style={styles.input} 
                   type="number" 
                   value={formData.price} 
                   onChange={(e) => setFormData({...formData, price: e.target.value})} 
                   placeholder="₹ 0" 
                 />
              </div>
              <div style={styles.inputField}>
                 <label style={styles.label}>{t(LangKeys.stock || 'Opening Stock')}</label>
                 <input 
                   style={styles.input} 
                   type="number" 
                   value={formData.stock} 
                   onChange={(e) => setFormData({...formData, stock: e.target.value})} 
                   placeholder="0" 
                 />
              </div>
           </div>
        </div>

        <div style={styles.footer}>
           <button style={styles.saveBtn} onClick={handleSave} disabled={loading}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              <span>{t(LangKeys.save || 'SAVE ITEM')}</span>
           </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { width: '100%', maxWidth: '400px', background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' },
  header: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' },
  content: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  inputField: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
  label: { fontSize: '12px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase' },
  input: { height: '48px', padding: '0 16px', borderRadius: '12px', border: '1px solid #E2E8F0', background: '#F8FAFC', fontSize: '15px', fontWeight: '600', outline: 'none' },
  row: { display: 'flex', gap: '16px' },
  footer: { padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9' },
  saveBtn: { width: '100%', height: '48px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }
};

export default QuickAddItemModal;
